import IconAction from "../../../shared/components/universal/icon-action"
import React, { useEffect, useState } from "react"
import { useForm } from "react-hook-form"
import { useTranslation } from "react-i18next"
import Checkbox from "../../../shared/components/fields/checkbox"
import RadioBox from "./selftest/radio-box"
import { Container } from "../../../react-bricks/components/layout"
import { Repeater, types } from "react-bricks/frontend"
import { scrollTo } from "../../../shared/utils/smooth-scroll"
import { toast } from "react-toastify"

/* global exponea */

type FormValues = {
    energy: string
    immune_system: string
    skin: string
    concentration: string
    vitality: string
    age: string
    checkbox: boolean
}

const Selftest: types.Brick = () => {
    const { t } = useTranslation("translations")

    const [isSuccess, setIsSuccess] = useState(false)

    useEffect(() => {
        if (isSuccess) {
            scrollTo("success_message")
        }
    }, [isSuccess])

    const {
        handleSubmit,
        reset,
        register,
        formState: { errors },
    } = useForm({
        mode: "onTouched",
        defaultValues: {
            energy: "",
            immune_system: "",
            skin: "",
            concentration: "",
            vitality: "",
            age: "",
            checkbox: false,
        },
        context: undefined,
        criteriaMode: "firstError",
        shouldFocusError: true,
    })

    const submit = (values: FormValues) => {
        const questions = {
            energy: t("selftest_form_energy_label"),
            immune_system: t("selftest_form_immune_system_label"),
            skin: t("selftest_form_skin_label"),
            concentration: t("selftest_form_concentration_label"),
            vitality: t("selftest_form_vitality_label"),
            age: t("selftest_form_age_label"),
        }

        Object.entries(questions).forEach(([field, question]) => {
            const answer = values[field as keyof typeof questions]

            // @ts-ignore
            if (exponea.isLoaded) {
                // @ts-ignore
                exponea.track("custom_survey", {
                    survey_name: "Selbsttest",
                    question: question,
                    answer: answer,
                    conditions_of_participation: true,
                })

                setIsSuccess(true)
                reset()
            } else {
                toast.error(t("biathlon_form_error") as string, { autoClose: 5000 })
                setIsSuccess(false)
            }
        })
    }

    const options = [
        t("selftest_form_intensity_option_1"),
        t("selftest_form_intensity_option_2"),
        t("selftest_form_intensity_option_3"),
    ]

    const ageOptions = [
        t("selftest_form_age_option_1"),
        t("selftest_form_age_option_2"),
        t("selftest_form_age_option_3"),
        t("selftest_form_age_option_4"),
    ]

    return (
        <Container>
            {!isSuccess ? (
                <div className="rounded-base bg-white p-10 sm:rounded-lg sm:p-16 lg:py-24 xl:px-40">
                    <h2 className="mb-5 font-gibson text-lg sm:text-xl lg:mb-7 lg:text-2xl">
                        {t("selftest_form_headline")}
                    </h2>
                    <p className="text-base sm:text-md md:flex md:flex-col lg:max-w-[970px] lg:text-lg">
                        <span className="font-semibold">{t("selftest_form_text_bold")}</span>
                        {t("selftest_form_text")}
                    </p>
                    <form onSubmit={handleSubmit(submit)} className="mt-10.5 max-w-full sm:mt-14 sm:gap-10 lg:mt-20">
                        <div className="flex flex-col gap-7 sm:gap-10">
                            <RadioBox
                                icon=""
                                headline={t("selftest_form_energy_headline")}
                                label={t("selftest_form_energy_label")}
                                options={options}
                                register={register("energy", {
                                    required: { value: true, message: t("form_error_required") },
                                })}
                                errors={errors}
                            />
                            <RadioBox
                                icon=""
                                headline={t("selftest_form_immune_system_headline")}
                                label={t("selftest_form_immune_system_label")}
                                options={options}
                                register={register("immune_system", {
                                    required: { value: true, message: t("form_error_required") },
                                })}
                                errors={errors}
                            />
                            <RadioBox
                                icon=""
                                headline={t("selftest_form_skin_headline")}
                                label={t("selftest_form_skin_label")}
                                options={options}
                                register={register("skin", {
                                    required: { value: true, message: t("form_error_required") },
                                })}
                                errors={errors}
                            />
                            <RadioBox
                                icon=""
                                headline={t("selftest_form_concentration_headline")}
                                label={t("selftest_form_concentration_label")}
                                options={options}
                                register={register("concentration", {
                                    required: { value: true, message: t("form_error_required") },
                                })}
                                errors={errors}
                            />
                            <RadioBox
                                icon=""
                                headline={t("selftest_form_vitality_headline")}
                                label={t("selftest_form_vitality_label")}
                                options={options}
                                register={register("vitality", {
                                    required: { value: true, message: t("form_error_required") },
                                })}
                                errors={errors}
                            />
                            <RadioBox
                                label={t("selftest_form_age_label")}
                                options={ageOptions}
                                errors={errors}
                                register={register("age", {
                                    required: { value: true, message: t("form_error_required") },
                                })}
                            />
                        </div>
                        <div className="my-10">
                            <Checkbox
                                register={register("checkbox", {
                                    required: { value: true, message: t("form_error_required") },
                                })}
                                error={errors}
                                label={t("selftest_form_checkbox")}
                            />
                        </div>
                        <IconAction submit button fullWidthMobile title={t("selftest_form_submit")} />
                    </form>
                </div>
            ) : (
                <div id="success_message">
                    <Repeater propName="successBox" />
                    <Repeater propName="aboTeaser" />
                    <Repeater propName="teaser" />
                    <Repeater propName="smallprint" itemProps={{ lessMargin: true }} />
                </div>
            )}
        </Container>
    )
}

Selftest.schema = {
    name: "Selftest",
    label: "Selftest Form",
    hideFromAddMenu: true,
    repeaterItems: [
        {
            name: "successBox",
            items: [
                {
                    type: "SuccessBox",
                    label: "Success Box",
                    min: 1,
                    max: 1,
                },
            ],
        },
        {
            name: "aboTeaser",
            items: [
                {
                    type: "AboTeaser",
                    label: "Abo Teaser",
                    max: 1,
                },
            ],
        },
        {
            name: "teaser",
            items: [
                {
                    type: "Teaser",
                    label: "Teaser",
                    max: 1,
                },
            ],
        },
        {
            name: "smallprint",
            items: [
                {
                    type: "Smallprint",
                    label: "Smallprint",
                },
            ],
        },
    ],
}

export default Selftest
